import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useApi } from '@/api/client';
import { matchNav } from '@/components/nav';
import { subscriptionTitle } from '@/types';

type Crumb = { label: string; to: string };

/**
 * The trail for the current route, derived rather than declared per page.
 *
 * Two sources, in order:
 *  1. the nav model (components/nav.ts), which already knows that /history is a
 *     Subscriptions page and that /ktinsights/history is called "Export history";
 *  2. whatever path segments are left over once the deepest nav entry is
 *     accounted for — in practice an id, resolved to something a person can read
 *     and otherwise shown raw.
 *
 * Nothing is rendered for a section landing page: one crumb is not a trail, and
 * the page's own <h1> already says where you are.
 */
export function Breadcrumbs() {
  const { pathname } = useLocation();
  const { section, item } = matchNav(pathname);

  // Path left over after the deepest thing the nav model names.
  const rest = item ? pathname.slice(item.to.length).split('/').filter(Boolean) : [];

  // Today the only id in a URL is a subscription's. The query is declared
  // unconditionally and disabled when there is nothing to resolve, so hook order
  // does not change with the route; it shares react-query's ['sub', id] entry
  // with the detail page, so it costs no extra request either way.
  const subscriptionId =
    section?.key === 'subscriptions' && item?.to === '/subscriptions' && rest.length === 1
      ? rest[0]
      : null;
  const resolvedName = useSubscriptionName(subscriptionId);
  const guideTitle = useGuideTitle(section?.key === 'help' && rest.length === 1 ? rest[0] : null);

  if (!section || !item) return null;

  const crumbs: Crumb[] = [{ label: section.label, to: section.to }];
  if (item.to !== section.to) crumbs.push({ label: item.crumb ?? item.label, to: item.to });

  let path = item.to;
  for (const segment of rest) {
    path = `${path}/${segment}`;
    // Falls back to the raw segment while the name is in flight or unavailable.
    // A spinner or a gap here would make the page look like it was still loading
    // long after it had finished.
    crumbs.push({
      label: segment === subscriptionId && resolvedName ? resolvedName : (guideTitle ?? segment),
      to: path
    });
  }

  if (crumbs.length < 2) return null;

  return (
    <nav className="breadcrumbs" aria-label="Breadcrumb">
      <ol>
        {crumbs.map((crumb, i) => (
          <li key={crumb.to}>
            {i === crumbs.length - 1
              ? <span aria-current="page">{crumb.label}</span>
              : <Link to={crumb.to}>{crumb.label}</Link>}
          </li>
        ))}
      </ol>
    </nav>
  );
}

/**
 * A help guide's title, from the same lazily loaded chunk the help pages use — importing the content
 * here directly would put every guide's words back into the bundle every page loads.
 */
function useGuideTitle(slug: string | null) {
  const { data } = useQuery({
    queryKey: ['help-guide-title', slug],
    queryFn: () => import('@/help/content').then(m => m.findGuide(slug!)?.title ?? null),
    enabled: Boolean(slug),
    staleTime: Infinity
  });
  return data ?? null;
}

function useSubscriptionName(id: string | null) {
  const api = useApi();
  const { data } = useQuery({
    queryKey: ['sub', id],
    queryFn: () => api.getSubscription(id!),
    enabled: Boolean(id)
  });
  // The subscription's own name when it has one, so the trail matches the heading it leads to.
  return data ? subscriptionTitle(data) : null;
}
